import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CommunityCardItem from '../components/CommunityCardItem';
import CommunityCardSkeleton from '../components/CommunityCardSkeleton';

const CommunityCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHashtag, setSelectedHashtag] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);

  // Check URL params for hashtag on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashtagParam = urlParams.get('hashtag');
    if (hashtagParam) {
      setSelectedHashtag(hashtagParam);
    }
  }, []);

  const fetchCommunityCards = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 96,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedHashtag && { hashtag: selectedHashtag }),
        ...(typeFilter && { type: typeFilter })
      });

      const response = await axios.get(`/api/community-cards?${params}`);
      setCards(response.data?.data || []);
      setTotalPages(response.data?.totalPages || 1);
      setTotalCards(response.data?.total || 0);
    } catch (err) {
      setError('Failed to load community cards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedHashtag, typeFilter]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [searchTerm, selectedHashtag, typeFilter]);

  useEffect(() => {
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      fetchCommunityCards();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [fetchCommunityCards]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already triggered by useEffect when searchTerm changes
    // This just ensures Enter key works
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Show skeletons while loading
  if (loading) {
    return (
      <div className="container">
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <h2 style={{
            color: 'var(--text-accent)',
            fontSize: '32px',
            fontWeight: '800',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            Gaming Community
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '16px',
            maxWidth: '600px',
            margin: '0 auto 24px',
            lineHeight: '1.6'
          }}>
            Connect with fellow gamers, share your passion, and build lasting friendships in our vibrant gaming community.
          </p>
        </div>

        <div className="grid">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both` }}>
              <CommunityCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        animation: 'fadeInUp 0.8s ease-out'
      }}>
        <h2 style={{
          color: 'var(--text-accent)',
          fontSize: '32px',
          fontWeight: '800',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          Gaming Community
        </h2>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '16px',
          maxWidth: '600px',
          margin: '0 auto 24px',
          lineHeight: '1.6'
        }}>
          Connect with fellow gamers, share your passion, and build lasting friendships in our vibrant gaming community.
        </p>

        {/* Filter Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          {[
            { id: '', label: 'All' },
            { id: 'hoster', label: 'Giveaway Hosts' },
            { id: 'creator', label: 'Content Creators' },
            { id: 'indie', label: 'Indie Devs' }
          ].map((type) => (
            <button
              key={type.id}
              onClick={() => {
                setTypeFilter(type.id);
                setCurrentPage(1);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                background: typeFilter === type.id ? 'var(--btn-primary)' : 'var(--bg-secondary)',
                color: typeFilter === type.id ? 'white' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.3s ease',
                boxShadow: typeFilter === type.id ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'
              }}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative', maxWidth: '400px', margin: '0 auto' }}>
            <input
              type="text"
              placeholder="Search community cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 45px 12px 16px',
                borderRadius: '25px',
                border: '2px solid var(--border-color)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: '16px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--btn-primary)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            />
            <button
              type="submit"
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--btn-primary)';
                e.target.style.background = 'rgba(102, 126, 234, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--text-secondary)';
                e.target.style.background = 'none';
              }}
            >
              🔍
            </button>
          </div>
        </form>

        {/* Active hashtag filter */}
        {selectedHashtag && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
            color: 'white',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '16px'
          }}>
            <span>#{selectedHashtag}</span>
            <button
              onClick={() => {
                setSelectedHashtag('');
                setCurrentPage(1);
                window.history.pushState({}, '', '/community');
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Results count */}
        {totalCards > 0 && (
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            Showing {cards.length} of {totalCards} community cards
            {selectedHashtag && ` with #${selectedHashtag}`}
          </p>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {cards.length === 0 ? (
        <div className="card" style={{
          textAlign: 'center',
          padding: '60px 20px',
          animation: 'fadeInUp 1s ease-out'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            opacity: 0.5
          }}>
            🎮
          </div>
          <h3 style={{
            color: 'var(--text-primary)',
            marginBottom: '12px',
            fontSize: '20px'
          }}>
            No Community Cards Yet
          </h3>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '16px',
            marginBottom: '24px'
          }}>
            Be the first to create your community card and showcase your gaming personality!
          </p>
          <a
            href="/dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, var(--btn-primary), var(--btn-primary-hover))',
              color: 'var(--text-light)',
              textDecoration: 'none',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            Create Your Card
          </a>
        </div>
      ) : (
        <>
          <div className="grid">
            {cards.map((card, index) => (
              <React.Fragment key={card._id}>
                <div className="card-fade-in" style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both` }}>
                  <CommunityCardItem card={card} />
                </div>
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginTop: '40px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              border: '2px solid var(--border-color)',
              background: currentPage === 1 ? 'var(--bg-secondary)' : 'var(--btn-primary)',
              color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text-light)',
              borderRadius: '8px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              opacity: currentPage === 1 ? 0.5 : 1
            }}
          >
            ← Previous
          </button>

          {/* Page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={{
                  padding: '8px 12px',
                  border: '2px solid var(--border-color)',
                  background: currentPage === pageNum ? 'var(--btn-primary)' : 'var(--bg-card)',
                  color: currentPage === pageNum ? 'var(--text-light)' : 'var(--text-primary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  minWidth: '40px'
                }}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              border: '2px solid var(--border-color)',
              background: currentPage === totalPages ? 'var(--bg-secondary)' : 'var(--btn-primary)',
              color: currentPage === totalPages ? 'var(--text-secondary)' : 'var(--text-light)',
              borderRadius: '8px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              opacity: currentPage === totalPages ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default CommunityCards;
